import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  useToast,
  Box,
  Text,
  Button,
  Grid,
  GridItem,
  VStack,
  Heading,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
} from "@chakra-ui/react";
import { AttachmentIcon, DownloadIcon } from '@chakra-ui/icons';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';

function FileUpload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [userFiles, setUserFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const fileInputRef = useRef(null);

  const fetchUserFiles = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const response = await callApi('/user/files', accessToken);
      const data = await response.json();
      if (response.ok) {
        setUserFiles(data.files || []);
      } else {
        throw new Error(data.error || 'Failed to fetch user files');
      }
    } catch (error) {
      toast({
        title: 'Error fetching files',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserFiles();
  }, [accessToken]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({ title: 'No file selected', status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get pre-signed URL from our backend
      const presignResponse = await callApi('/user/generate_presigned_url', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: selectedFile.name,
          file_size: selectedFile.size,
          file_type: selectedFile.type,
        }),
      });

      const presignData = await presignResponse.json();
      if (!presignResponse.ok) {
        throw new Error(presignData.error || 'Could not get upload URL.');
      }

      const { file_id, s3_key, presigned_data } = presignData;
      const { url, fields } = presigned_data;

      // Step 2: Upload the file directly to S3
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', selectedFile);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
        }
      };

      xhr.onload = async () => {
        if (xhr.status === 204) {
          // Step 3: Notify our backend that the upload was successful
          try {
            await callApi('/user/update_upload_status', accessToken, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ file_id, s3_key }),
            });
            
            toast({
              title: 'Upload Successful',
              description: `${selectedFile.name} has been uploaded.`,
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            fetchUserFiles(); // Refresh the file list
          } catch (error) {
             toast({
              title: 'Upload confirmation failed',
              description: 'The file was uploaded to storage, but we could not confirm it. Please contact support.',
              status: 'error',
              duration: 9000,
              isClosable: true,
            });
          }
        } else {
          toast({
            title: 'Upload Failed',
            description: 'The file could not be uploaded. Please try again.',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        }
        // Reset state regardless of outcome
        setIsUploading(false);
        setSelectedFile(null);
        setUploadProgress(0);
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
      };

      xhr.onerror = () => {
        toast({
          title: 'Upload Error',
          description: 'A network error occurred during the upload.',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
        setIsUploading(false);
      };

      xhr.send(formData);

    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsUploading(false);
    }
  };

  const handleDownload = async (fileId) => {
    try {
      const response = await callApi(`/user/file/${fileId}/download_url`, accessToken);
      const data = await response.json();

      if (response.ok) {
        // Open the secure, temporary URL in a new tab to trigger the download
        window.open(data.download_url, '_blank');
      } else {
        throw new Error(data.error || 'Failed to get download link');
      }
    } catch (error) {
      toast({
        title: 'Download Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box fontSize="sm">
      <Grid
        templateAreas={`"header" "main"`}
        gridTemplateRows={'60px 1fr'}
        h='100vh'
      >
        <HeaderMenus />
        <GridItem bg='white' padding={8} area={'main'}>
          <VStack spacing={8} align="stretch">
            <Heading size="lg">My Files</Heading>
            
            <Box p={6} borderWidth="1px" borderRadius="md" borderColor="gray.200">
              <VStack spacing={4} align="stretch">
                <Heading size="md">New Upload</Heading>
                <Text>Select a file to upload to your secure storage.</Text>
                
                <input
                  type="file"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                />
                
                <HStack>
                  <Button
                    leftIcon={<AttachmentIcon />}
                    onClick={() => fileInputRef.current.click()}
                    isDisabled={isUploading}
                  >
                    Choose File
                  </Button>
                  {selectedFile && <Text fontSize="md">{selectedFile.name}</Text>}
                </HStack>

                {isUploading && <Progress value={uploadProgress} size="md" colorScheme="green" hasStripe isAnimated />}

                <Button
                  colorScheme="green"
                  onClick={handleUpload}
                  isDisabled={!selectedFile || isUploading}
                  isLoading={isUploading}
                  loadingText={`Uploading... ${uploadProgress}%`}
                >
                  Upload File
                </Button>
              </VStack>
            </Box>

            <Box>
              <Heading size="md" mb={4}>Uploaded Files</Heading>
              <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                <Table variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Filename</Th>
                      <Th>Size</Th>
                      <Th>Upload Date</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {isLoading ? (
                      <Tr><Td colSpan="4" textAlign="center">Loading...</Td></Tr>
                    ) : userFiles.length > 0 ? (
                      userFiles.map(file => (
                        <Tr key={file.id}>
                          <Td fontWeight="medium">{file.filename}</Td>
                          <Td>{formatFileSize(file.size)}</Td>
                          <Td>{new Date(file.created_at).toLocaleString()}</Td>
                          <Td>
                            <Button
                              size="sm"
                              leftIcon={<DownloadIcon />}
                              onClick={() => handleDownload(file.id)}
                            >
                              Download
                            </Button>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr><Td colSpan="4" textAlign="center">No files found.</Td></Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
}

export default FileUpload;